/**
 * WorkflowErrorsPage - Submenu "Erros do processo" do módulo Workflow
 * -----------------------------------------------------------------------------
 * Tabela paginada das falhas definitivas (DLQ) com filtros por situação e
 * processo, detalhe do erro e atalhos para a execução e para o registro de
 * origem. A resolução (reprocessar, ignorar, cancelar, compensar) reaproveita
 * `useWorkflowDeadLetters().resolve`; o banco revalida tenant e permissão.
 *
 * Permissões: ver = `workflow_errors` (view); resolver = `workflow_errors` (edit).
 *
 * @version 1.1.0
 * RELEASE: 2026-10-06 v1.1.0 - Botão "Detalhes" com estilo suave (fundo primary/10 +
 *          texto primary) no lugar do sólido, em linha com o restante das ações.
 * RELEASE: 2026-10-06 v1.0.0 - Criação inicial (submenus do Workflow).
 */

import { Fragment, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Activity, Ban, ExternalLink, EyeOff, RotateCcw, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PaginationFooter } from '@/components/ui/pagination-footer';
import { QUICK_FILTER_PILL } from '@/components/layout/list-page/quickFilterStyles';
import {
  ListPageContent,
  ListPageFiltersBar,
  ListPageFooter,
  ListPageHeader,
  ListPageLayout,
} from '@/components/layout/list-page';
import { WorkflowDefinitionFilter } from '@/components/workflow/WorkflowDefinitionFilter';
import { WorkflowExecutionsDialog } from '@/components/workflow/WorkflowExecutionsDialog';
import {
  WORKFLOW_ENTITY_LABEL,
  canOpenWorkflowRecord,
  useWorkflowRecordOpener,
} from '@/components/workflow/WorkflowRecordOpener';
import { useWorkflowDeadLetters, type WorkflowDlqAction } from '@/hooks/useWorkflowDeadLetters';
import { usePermissions } from '@/hooks/usePermissions';
import { formatUuidShort } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const dataHora = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

/** Rótulos das situações da fila de erros */
const SITUACAO: Record<string, string> = {
  aberto: 'Em aberto',
  reprocessado: 'Reprocessado',
  resolvido: 'Resolvido',
};

export default function WorkflowErrorsPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get('status') || 'aberto');
  const [processo, setProcesso] = useState<string | null>(null);
  const [pagina, setPagina] = useState(1);
  const [tamanho, setTamanho] = useState(25);
  const [expandido, setExpandido] = useState<string | null>(null);
  const [justificativas, setJustificativas] = useState<Record<string, string>>({});
  const [execucoesAbertas, setExecucoesAbertas] = useState(false);

  const { canEdit } = usePermissions();
  const podeResolver = canEdit('workflow_errors');
  const { openRecord, element: fichas } = useWorkflowRecordOpener();

  useEffect(() => setPagina(1), [status, processo, tamanho]);

  const { itens, total, isLoading, resolve } = useWorkflowDeadLetters(status, {
    definitionId: processo,
    limit: tamanho,
    offset: (pagina - 1) * tamanho,
  });

  /** Dispara a resolução escolhida (justificativa validada no banco) */
  const resolver = (id: string, acao: WorkflowDlqAction) =>
    resolve.mutate({ id, acao, justificativa: justificativas[id]?.trim() ?? '' });

  return (
    <ListPageLayout>
      <ListPageHeader
        title="Erros do processo"
        description="Falhas definitivas das ações automáticas. Nada é descartado: tudo fica registrado."
        actions={
          <Button size="sm" variant="outline" className="gap-1" onClick={() => setExecucoesAbertas(true)}>
            <Activity className="h-4 w-4" /> Execuções com erro
          </Button>
        }
      />

      <ListPageFiltersBar>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className={cn(QUICK_FILTER_PILL, 'w-auto min-w-[140px]')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="aberto">Em aberto</SelectItem>
            <SelectItem value="reprocessado">Reprocessados</SelectItem>
            <SelectItem value="resolvido">Resolvidos</SelectItem>
            <SelectItem value="todos">Todos</SelectItem>
          </SelectContent>
        </Select>
        <WorkflowDefinitionFilter value={processo} onChange={setProcesso} />
      </ListPageFiltersBar>

      <ListPageContent>
        <div className="overflow-auto">
          <table className="w-full min-w-[900px] text-xs">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Ação</th>
                <th className="px-3 py-2 text-left font-medium">Processo</th>
                <th className="px-3 py-2 text-left font-medium">Registro</th>
                <th className="px-3 py-2 text-left font-medium">Erro</th>
                <th className="px-3 py-2 text-left font-medium">Tentativas</th>
                <th className="px-3 py-2 text-left font-medium">Data</th>
                <th className="px-3 py-2 text-left font-medium">Situação</th>
                <th className="px-3 py-2 text-right font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">Carregando…</td></tr>
              )}
              {!isLoading && itens.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground">Nenhum erro para estes filtros.</td></tr>
              )}

              {itens.map((item) => {
                const aberto = expandido === item.id;
                return (
                  <Fragment key={item.id}>
                    <tr className={cn('border-t', item.status === 'aberto' && 'bg-destructive/5')}>
                      <td className="max-w-[200px] px-3 py-2">
                        <p className="truncate font-medium text-foreground">{item.action_label || item.action_code}</p>
                        {item.is_compensation && (
                          <Badge variant="outline" className="mt-0.5 h-4 px-1.5 text-[10px]">Compensação</Badge>
                        )}
                      </td>
                      <td className="max-w-[180px] truncate px-3 py-2">{item.definition_name || '—'}</td>
                      <td className="px-3 py-2">
                        <p>{item.root_entity_type ? WORKFLOW_ENTITY_LABEL[item.root_entity_type] ?? item.root_entity_type : '—'}</p>
                        {item.root_entity_id && (
                          <p className="font-mono text-muted-foreground">{formatUuidShort(item.root_entity_id)}</p>
                        )}
                      </td>
                      <td className="max-w-[260px] px-3 py-2">
                        <p className="truncate text-foreground" title={item.error_message ?? ''}>{item.error_message || '—'}</p>
                        <p className="text-[10px] text-muted-foreground">{item.error_class}{item.error_code ? ` · ${item.error_code}` : ''}</p>
                      </td>
                      <td className="px-3 py-2">{item.attempts}</td>
                      <td className="px-3 py-2 text-muted-foreground">{dataHora(item.created_at)}</td>
                      <td className="px-3 py-2">{SITUACAO[item.status] ?? item.status}</td>
                      <td className="px-3 py-2">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" className="h-7 gap-1 text-xs"
                            disabled={!canOpenWorkflowRecord(item.root_entity_type)}
                            onClick={() => openRecord(item.root_entity_type, item.root_entity_id)}>
                            <ExternalLink className="h-3.5 w-3.5" /> Abrir registro
                          </Button>
                          {/* v1.1.0: estilo suave (primary/10 + primary) no lugar do sólido */}
                          <Button
                            size="sm"
                            variant="outline"
                            className={cn('h-7 text-xs', !aberto && 'border-primary/20 bg-primary/10 text-primary hover:bg-primary/15')}
                            onClick={() => setExpandido(aberto ? null : item.id)}
                          >
                            {aberto ? 'Fechar' : 'Detalhes'}
                          </Button>
                        </div>
                      </td>
                    </tr>

                    {/* Detalhe completo + resolução */}
                    {aberto && (
                      <tr className="bg-muted/20">
                        <td colSpan={8} className="space-y-2 px-3 py-3">
                          <p className="whitespace-pre-wrap break-words text-xs text-foreground">{item.error_message}</p>
                          {item.resolution_reason && (
                            <p className="text-xs text-muted-foreground">
                              Resolução: {item.resolution_reason} · {dataHora(item.resolved_at)}
                            </p>
                          )}
                          {item.status === 'aberto' && (
                            <>
                              <Textarea
                                rows={2}
                                placeholder="Justificativa obrigatória (mínimo 5 caracteres)"
                                value={justificativas[item.id] ?? ''}
                                maxLength={500}
                                onChange={(e) =>
                                  setJustificativas((a) => ({ ...a, [item.id]: e.target.value.slice(0, 500) }))
                                }
                              />
                              <div className="flex flex-wrap justify-end gap-2">
                                <Button size="sm" variant="outline" disabled={!podeResolver || resolve.isPending}
                                  onClick={() => resolver(item.id, 'ignorar')}>
                                  <EyeOff className="mr-1 h-4 w-4" /> Ignorar
                                </Button>
                                <Button size="sm" variant="outline" disabled={!podeResolver || resolve.isPending}
                                  onClick={() => resolver(item.id, 'cancelar_instancia')}>
                                  <Ban className="mr-1 h-4 w-4" /> Cancelar processo
                                </Button>
                                <Button size="sm" variant="outline" disabled={!podeResolver || resolve.isPending}
                                  onClick={() => resolver(item.id, 'compensar')}>
                                  <Undo2 className="mr-1 h-4 w-4" /> Compensar
                                </Button>
                                <Button size="sm" disabled={!podeResolver || resolve.isPending}
                                  onClick={() => resolver(item.id, 'reprocessar')}>
                                  <RotateCcw className="mr-1 h-4 w-4" /> Reprocessar
                                </Button>
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </ListPageContent>

      <ListPageFooter>
        <PaginationFooter
          totalItems={total}
          currentPage={pagina}
          pageSize={tamanho}
          onPageChange={setPagina}
          onPageSizeChange={setTamanho}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </ListPageFooter>

      {/* Atalho para as execuções com erro (mesmo painel de observabilidade) */}
      <WorkflowExecutionsDialog
        open={execucoesAbertas}
        onOpenChange={setExecucoesAbertas}
        statusInicial="com_erro"
      />
      {fichas}
    </ListPageLayout>
  );
}
